import { FormProvider, getFormProps } from '@conform-to/react'
import { Box, Button, Callout, Flex } from '@radix-ui/themes'
import { useLocation, useSearchParams } from '@remix-run/react'
import * as React from 'react'
import { RxInfoCircled } from 'react-icons/rx'
import { z } from 'zod'
import { FormDefaults } from '#app/components/common/form-defaults.tsx'
import { SubmissionButton } from '#app/components/common/submission-button.tsx'
import { TextField } from '#app/components/ui/text-field.tsx'
import { type Locale } from '#app/models/locale.ts'
import { NEWSLETTER_SEARCH_PARAMS } from '#app/routes/($lang).newsletter/route-config'
import { type App } from '#app/routes/_layout/component.route.tsx'
import { type FormEnum } from '#app/routes/_layout/route-config.ts'
import { testId } from '#app/utils/client/test-id.ts'
import { schemaDefault } from '#app/utils/form-defaults.ts'
import { useNewsletterForm } from '#app/utils/hooks/forms/use-newsletter-form.ts'

/* -------------------------------------------------------------------------------------------------
 * Newsletter form
 * -----------------------------------------------------------------------------------------------*/

enum TEST_ID {
	newsletter = 'newsletter',
}

enum INPUTS {
	email = 'newsletterEmail',
}

const FORM: FormEnum = {
	id: 'subscribe-newsletter-form',
	action: '/newsletter',
	fetcherKey: 'subscribe-newsletter',
	subaction: 'subscribe-newsletter',
	redirect: '/newsletter',
} as const

const schema = z.object({
	...schemaDefault,
	...{
		[INPUTS.email]: z
			.string({ required_error: 'validation.email.required' })
			.email('validation.email.email')
			.max(254, 'validation.email.max'),
	},
})

type NewsletterFormProps = {
	locale: Locale
	setApp: React.Dispatch<React.SetStateAction<App>>
}

const NewsletterForm: React.FC<NewsletterFormProps> = ({ locale }) => {
	const {
		newsletterFetcher,
		newsletterFormValues,
		newsletterForm,
		newsletterFields,
		t,
	} = useNewsletterForm()

	const location = useLocation()
	const [searchParams] = useSearchParams()

	const isSubscribed = React.useMemo(
		() => newsletterFetcher.data?.status === 201,
		[newsletterFetcher.data?.status],
	)

	React.useEffect(() => {
		if (isSubscribed) {
			newsletterForm.reset()
		}
	}, [newsletterFetcher.data?.status, isSubscribed, newsletterForm])

	return (
		<FormProvider
			context={newsletterForm.context}
			{...testId(TEST_ID.newsletter)}
		>
			<Flex asChild direction="column" gapY="4">
				<newsletterFetcher.Form
					method="post"
					action={`/${locale}${FORM.action}${location.search}`}
					{...getFormProps(newsletterForm)}
					preventScrollReset
				>
					<FormDefaults
						form={newsletterForm}
						fields={newsletterFields}
						values={newsletterFormValues}
					/>
					{(searchParams.get(NEWSLETTER_SEARCH_PARAMS.newsletterSubscribed) ===
						'on' ||
						isSubscribed) && (
						<Box mb="4">
							<Callout.Root>
								<Callout.Icon>
									<RxInfoCircled />
								</Callout.Icon>
								<Callout.Text>{t('alert.newsletterSubscribed')}</Callout.Text>
							</Callout.Root>
						</Box>
					)}
					<TextField
						type="email"
						meta={newsletterFields[INPUTS.email]}
						t={t}
						disabled={isSubscribed}
					>
						{t('label.email')}
					</TextField>
					<Button
						size="4"
						variant="surface"
						type="submit"
						disabled={isSubscribed}
					>
						<SubmissionButton fetcher={newsletterFetcher}>
							{t('button.newsletterSubmit')}
						</SubmissionButton>
					</Button>
				</newsletterFetcher.Form>
			</Flex>
		</FormProvider>
	)
}
NewsletterForm.displayName = 'NewsletterForm'

/* -----------------------------------------------------------------------------------------------*/

export {
	type NewsletterFormProps,
	NewsletterForm,
	TEST_ID as NEWSLETTER_TEST_ID,
	INPUTS as NEWSLETTER_INPUTS,
	FORM as NEWSLETTER_FORM,
	schema as newsletterSchema,
}
